import border from '@/assets/100000.json'
import provinces from '@/assets/100000_full.json'
import cities from '@/assets/100000_full_city.json'
import { mapPresentation, mapPresentationDefaults, mapSurface, mapTint, mapClusterBadge, shortMapName, mapTooltip, mapTooltipPosition } from './presentation'
import { MAP_MAX_ZOOM, mapInteraction, initialMapView, isRegionCountMode } from './interaction'
import { regionLabelItem, regionHoverSeries, regionAnchorSeries } from './region-labels'

export const provinceChoices = provinces.features.filter(feature => feature.properties && feature.properties.name)
  .map(feature => ({ id: String(feature.properties.adcode), name: feature.properties.name }))

// 仅显式演示地图使用这层迁移，旧用户mapData和视口配置保持原行为。
export function mapRenderOptions (input = {}, dynamic = false) {
  const option = { ...input }, demo = input.demoMap === true || (input.demoMapData && typeof input.demoMapData === 'object')
  if (!demo) return option
  if (dynamic) {
    if (input.demoMap === true) delete option.mapData
    delete option.demoMapData; delete option.demoBackgroundImage
    for (const key of ['areaColor', 'empAreaColor', 'borderColor', 'borderWidth']) option[key] = mapPresentationDefaults[key]
    delete option.centerLng; delete option.centerLat
    option.regionCode = '100000'; option.zoom = 1
    if (!['country', 'province', 'city'].includes(option.mapLevel)) option.mapLevel = 'province'
  } else if (input.demoMapData && input.demoMapData.value && input.demoMapData.value.type === 'FeatureCollection') {
    option.mapData = input.demoMapData; option.regionCode = ''
  }
  return option
}

export function demoMapBackground (option = {}, dynamic = false) {
  const src = option.demoBackgroundImage
  if (dynamic || option.demoMap !== true || typeof src !== 'string' || src.length > 3000000) return ''
  return /^(?:https?:\/\/|\/(?!\/)|\.?\.?\/|ui\/|img\/)/i.test(src) || /^data:image\/(?:png|jpe?g|webp|svg\+xml)[;,]/i.test(src) ? src : ''
}

export function localBasemap (option = {}) {
  if (isRegionCountMode(option)) return localBasemap({ ...option, displayMode: 'devices', regionCode: option.regionCode || '100000', mapLevel: option.regionCountLevel === 'city' ? 'city' : 'province' })
  if (!option.regionCode && option.mapData && option.mapData.value && option.mapData.value.type === 'FeatureCollection') return option.mapData.value
  const region = String(option.regionCode || '100000'), detail = option.mapLevel === 'city'
  if (region === '100000') return option.mapLevel === 'country' ? border : detail ? cities : provinces
  const own = provinces.features.filter(feature => String(feature.properties.adcode) === region)
  if (!own.length) throw new Error('所选本地地图区域不存在，请重新选择')
  const children = cities.features.filter(feature => feature.properties && feature.properties.parent && String(feature.properties.parent.adcode) === region)
  return { type: 'FeatureCollection', features: detail && children.length ? children : own }
}

export const countryOutline = border

// 先解决同位置设备互相遮挡；不把行政区中心当成设备实际位置。
export function clusterSameLocation (rows) {
  const groups = new Map()
  rows.forEach(row => { const key = row.lng + ':' + row.lat; if (!groups.has(key)) groups.set(key, []); groups.get(key).push(row) })
  return Array.from(groups.values()).map(group => group.length === 1 ? group[0] : {
    ...group[0], name: group.length + ' 台设备',
    state: group.every(row => row.state === group[0].state) ? group[0].state : 'unknown', clusterRows: group
  })
}

// 仅聚合当前视野的绘制对象，原始位置仍完整保存在数据层；缩放/平移后重新分桶。
export function clusterMapPoints (rows, project, { width, height, cellSize = 80, highlightUntil } = {}) {
  if (typeof project !== 'function') return clusterSameLocation(rows)
  const size = Math.max(24, Number(cellSize) || 80), groups = new Map()
  rows.forEach(row => {
    const pixel = project([row.lng, row.lat], row)
    if (!Array.isArray(pixel) || pixel.length !== 2 || !pixel.every(Number.isFinite)) return
    if ((width > 0 && (pixel[0] < -size || pixel[0] > width + size)) ||
        (height > 0 && (pixel[1] < -size || pixel[1] > height + size))) return
    const key = Math.floor(pixel[0] / size) + ':' + Math.floor(pixel[1] / size)
    if (!groups.has(key)) groups.set(key, { count: 0, lng: 0, lat: 0, samples: [], state: row.state, bounds: [row.lng, row.lat, row.lng, row.lat] })
    const group = groups.get(key)
    if (highlightUntil) group.motionUntil = Math.max(group.motionUntil || 0, highlightUntil(row.deviceId))
    group.count++; group.lng += row.lng; group.lat += row.lat
    group.bounds[0] = Math.min(group.bounds[0], row.lng); group.bounds[1] = Math.min(group.bounds[1], row.lat)
    group.bounds[2] = Math.max(group.bounds[2], row.lng); group.bounds[3] = Math.max(group.bounds[3], row.lat)
    if (group.samples.length < 8) group.samples.push(row)
    if (group.state !== row.state) group.state = 'unknown'
  })
  return Array.from(groups.values()).map(group => group.count === 1 ? group.samples[0] : {
    name: group.count + ' 台设备', lng: group.lng / group.count, lat: group.lat / group.count,
    state: group.state, clusterCount: group.count, clusterRows: group.samples, bounds: group.bounds,
    ...(group.motionUntil ? { motionUntil: group.motionUntil } : {})
  })
}

// 使用聚合的完整范围决定放大幅度；不是仅看8个悬停样本，也不挪动重合设备坐标。
export function clusterViewport (row, geo, width, height, project) {
  const bounds = row.bounds, current = Math.max(.5, Number(geo.zoom) || 1)
  let factor = 2, center = [row.lng, row.lat]
  if (Array.isArray(bounds) && bounds.length === 4 && bounds.every(Number.isFinite)) {
    center = [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2]
    if ((bounds[0] !== bounds[2] || bounds[1] !== bounds[3]) && typeof project === 'function') {
      const first = project(bounds.slice(0, 2)), last = project(bounds.slice(2))
      if ([first, last].every(pixel => Array.isArray(pixel) && pixel.every(Number.isFinite))) {
        factor = Math.max(2, Math.min(width * .45 / Math.max(1, Math.abs(last[0] - first[0])), height * .45 / Math.max(1, Math.abs(last[1] - first[1]))))
      }
    }
  }
  return { zoom: Math.min(MAP_MAX_ZOOM, current * factor), center }
}

export function mapChartOption (input, mapName, rows, regionLayouts) {
  const option = mapPresentation(input), interaction = mapInteraction(option), glow = Math.max(0, Math.min(100, Number(option.glowStrength) || 0)) / 100
  const series = [{
    id: 'device-locations', name: '设备位置',
    type: 'scatter', coordinateSystem: 'geo', geoIndex: 0, z: 6,
    symbol: ['circle', 'pin', 'diamond', 'rect'].includes(option.pointSymbol) ? option.pointSymbol : 'circle',
    symbolSize: Math.max(3, Math.min(40, Number(option.pointSize) || 10)),
    label: { normal: { show: option.showDeviceLabel === true, position: 'right', formatter: item => shortMapName(item.name, option.fontSize), color: option.color || '#d9edf4', fontSize: Number(option.fontSize) || 12 } },
    itemStyle: { normal: { color: option.pointColor, opacity: 1, shadowColor: mapTint(option.pointColor, 0, .7), shadowBlur: 18 * glow },
      emphasis: { shadowBlur: 26 * glow, borderColor: '#c8f6ff', borderWidth: 1 } },
    data: rows.map(row => {
      const item = { name: row.name, value: [row.lng, row.lat, row.value === undefined ? 1 : row.value], device: row }
      if (row.regionCount) return regionLabelItem(row, option, regionLayouts && regionLayouts.get(String(row.regionId || row.name)))
      if (!row.clusterRows) return item
      const badge = mapClusterBadge(row.clusterCount || row.clusterRows.length, option)
      return { ...item, symbol: badge.symbol, symbolSize: [badge.width, badge.height], symbolOffset: [0, badge.offset],
        itemStyle: { normal: { opacity: 1, shadowBlur: 8 * glow, shadowColor: mapTint(option.clusterColor, 0, .3) } },
        label: { normal: { show: true, position: 'inside', offset: [0, badge.labelOffset], formatter: '{count|' + badge.label + '}{unit| 台}',
          rich: { count: { color: '#f3fbff', fontSize: badge.fontSize, fontFamily: 'Arial, "Microsoft YaHei", sans-serif', fontWeight: 600 },
            unit: { color: badge.accent, fontSize: badge.unitSize, padding: [3, 0, 0, 2] } } },
          emphasis: { show: true } } }
    })
  }]
  if (isRegionCountMode(option) || rows.some(row => row.regionCount)) series.push(regionHoverSeries(), regionAnchorSeries(rows.filter(row => row.regionCount), option, regionLayouts))
  const surface = { id: 'device-map-surface', map: mapName, z: 2, animation: false, roam: interaction.roam, ...initialMapView(option),
    scaleLimit: { min: .5, max: MAP_MAX_ZOOM }, aspectScale: 1, layoutCenter: ['50%', '50%'], layoutSize: '90%',
    label: { normal: { show: !isRegionCountMode(option) && option.showRegionLabel === true, color: option.color || '#9dbdce', fontSize: Number(option.fontSize) || 12 }, emphasis: { show: !isRegionCountMode(option) && option.showRegionLabel === true, color: option.empColor || '#fff' } },
    itemStyle: { normal: { areaColor: mapSurface(option.areaColor), borderColor: option.borderColor, borderWidth: option.borderWidth === undefined ? .7 : Number(option.borderWidth),
      shadowColor: mapTint(option.borderColor, 0, .25), shadowBlur: 6 * glow },
      emphasis: { areaColor: mapSurface(option.empAreaColor), borderColor: mapTint(option.borderColor, .3) } } }
  return {
    backgroundColor: option.backgroundColor || 'transparent', animationDurationUpdate: 150, animationDuration: 250,
    // 旧配置即使开启过状态图例，也不在地图中展示设备在线情况。
    legend: { show: false },
    tooltip: { show: option.showInfo !== false, trigger: 'item', triggerOn: 'mousemove|click', confine: true, enterable: true,
      backgroundColor: 'rgba(5,24,42,.97)', borderColor: option.clusterColor, borderWidth: 1, padding: [12, 14],
      textStyle: { color: '#e7f4ff', fontSize: 14 }, hideDelay: 120,
      extraCssText: 'max-width:284px;white-space:normal;overflow-wrap:anywhere;box-sizing:border-box;border-radius:5px;box-shadow:0 5px 18px rgba(0,0,0,.3);',
      position: mapTooltipPosition, formatter: item => {
      const row = item.data && item.data.device
      return mapTooltip(row, interaction.zoom, option)
    } },
    geo: [surface], series
  }
}
